import type { HotelOption, IPolicyOverride } from '@/lib/allocation/types';
import type { ReservationStatus } from '@/lib/booking/types';

// Shared UI-state types for the allocation component family (reusable across the
// disruption-event scope and the single-case scope). These are operator-session
// UI types — not domain shapes. See docs/ui/reusable-allocation-components.plan.md.
// This module imports only from @/lib/* — never from a route folder. It also
// carries the pure hold-CLOCK model (below): deriving a countdown from a server
// timestamp is state, not presentation, and keeping it out of the component
// files lets them export components only (react-refresh).

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/** Countdown derived from a SERVER deadline. `hasClock: false` is a first-class
 *  state (no deadline granted), NOT a lapsed one — `lapsed` stays false. */
export interface HoldClock {
  hasClock: boolean;
  lapsed: boolean;
  msLeft: number;
  /** Coarse-to-fine remaining, e.g. "1h 05m" / "12m 30s" / "45s". '' with no clock. */
  remaining: string;
  /** Absolute local wall-clock the deadline falls on, e.g. "14:32". '' with no clock. */
  until: string;
}

const NO_CLOCK: HoldClock = { hasClock: false, lapsed: false, msLeft: 0, remaining: '', until: '' };

/** "1h 05m" / "12m 30s" / "45s" — the house countdown format. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (total >= 3600) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (total >= 60) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** "1:05:09" / "05:09" / "2d 01:05:09" — the LIVE countdown face: seconds always
 *  shown, so the operator sees it move. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

/** The operator's local wall-clock time for an instant, e.g. "14:32". */
export function formatUntil(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Resolve a server deadline against `now`. An absent or unparseable timestamp
 * yields the no-clock state — we never invent a deadline, and never round a
 * missing one down to zero.
 */
export function holdClock(expiresAt: string | null | undefined, now: number): HoldClock {
  if (!expiresAt) return NO_CLOCK;
  const at = new Date(expiresAt);
  const ms = at.getTime();
  if (Number.isNaN(ms)) return NO_CLOCK;
  const msLeft = ms - now;
  return {
    hasClock: true,
    lapsed: msLeft <= 0,
    msLeft: Math.max(0, msLeft),
    remaining: formatRemaining(msLeft),
    until: formatUntil(at),
  };
}

/**
 * Tone ladder for the HARD passenger-acceptance clock. Unlike the soft
 * inventory hold, this window really does close, so it escalates as it drains.
 */
export function acceptanceTone(clock: HoldClock): 'muted' | 'neutral' | 'warning' | 'error' {
  if (!clock.hasClock || clock.lapsed) return 'muted';
  if (clock.msLeft <= 30 * MINUTE_MS) return 'error';
  if (clock.msLeft <= 2 * HOUR_MS) return 'warning';
  return 'neutral';
}

/**
 * A soft-hold the operator placed but has not yet confirmed — the UI's own state.
 *
 * The hold deadline is SERVER truth: it arrives on the /v1/booking/hold response
 * and is stored verbatim. The UI used to fabricate one (`heldAt` + a hardcoded
 * 90s constant) which no longer matched any deployment — booking's default is
 * 300s in prod and 1800s on the local stack — so cards read "Hold expired" with
 * half an hour of real hold left, hid Confirm and pushed a pointless Re-hold.
 * There is now no client-side TTL constant: no server clock means NO countdown,
 * never a zero.
 */
export interface PendingHold {
  /** Local card id — stable across Re-hold so the card doesn't jump. */
  id: string;
  /** Ownership token from /v1/booking/hold; threaded into /v1/booking/book. */
  attemptUrn: string;
  offer: HotelOption;
  subCaseUrn: string;
  roomCount: number;
  /** epoch ms when this client placed the hold — provenance only. NEVER a
   *  deadline: the only deadline is `expiresAt`, which the server grants. */
  heldAt: number;
  /** Server-truth hold deadline (ISO), from the hold response. A live booking
   *  domain always sends one — it 422s rather than granting a clockless hold —
   *  so in practice this is absent only in the BFF's offline/fixture mode
   *  (BOOKING_BASE_URL unset), which is a real mode on this stack. Absent means
   *  the card shows "Hold active" with no countdown and keeps every action. */
  expiresAt?: string | undefined;
  /** The TTL the server granted this hold, for provenance in the tooltip. */
  ttlSeconds?: number | undefined;
  /** Whose inventory clock this is — ours or the vendor's. */
  source?: 'INTERNAL' | 'VENDOR' | undefined;
  /** Stable Idempotency-Key for the eventual book; reused on retry, never regenerated. */
  bookKey: string;
  /** In-flight action for this card (disables its buttons + shows a spinner). */
  busy?: 'confirm' | 'release' | 'rehold' | undefined;
  /** Surfaced when a confirm/re-hold call failed (e.g. a 4xx on a reaped hold). */
  error?: string | undefined;
  /** Chosen OUTSIDE the policy: the person's reason and the rules it breaks — sent with
   *  the book, so the reservation records it. */
  policyOverride?: IPolicyOverride | undefined;
}

/**
 * A group that has been confirmed (booked). Reservations-by-case carries no
 * sub-case link, so once a hold is consumed by confirm we'd otherwise lose the
 * group → hotel mapping. This records it so the board can keep showing the group
 * as "Booked at <hotel>". Cleared when the matching reservation is cancelled.
 */
export interface ConfirmedAssignment {
  subCaseUrn: string;
  hotelUrn: string;
  hotelName: string;
  reservationUrn: string;
  rooms: number;
}

/** Per-group (sub-case) lock state, resolved for one group. */
export interface GroupLock {
  isMine: boolean;
  isHeld: boolean;
  holderName: string | null;
  expiresAt: string | null;
}

/** A group's current placement on the board: a live hold, or a booked reservation. */
export type GroupAssignment =
  | { kind: 'held'; hotelUrn: string; hotelName: string; hold: PendingHold }
  | {
      kind: 'booked';
      hotelUrn: string;
      hotelName: string;
      reservationUrn: string;
      status: ReservationStatus;
    };
